'use client';

import { useTransition } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ps/ui/atoms/alert-dialog';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Runs the leave; resolves once the API call succeeds. */
  onConfirm: () => Promise<void>;
}

/**
 * Confirm leaving a community channel — mirrors PopshopRN's leave confirm
 * ("Are you sure you want to leave this channel?", destructive). Explicit dark
 * colors because the dashboard shell isn't `.dark`-scoped and `AlertDialog`
 * portals to `body` (sibling {@link DeleteChannelMessageDialog}).
 */
export function LeaveChannelDialog({ open, onOpenChange, onConfirm }: Props) {
  const [pending, startTransition] = useTransition();

  const handleConfirm = () => {
    if (pending) return;
    startTransition(async () => {
      try {
        await onConfirm();
        onOpenChange(false);
      } catch (error) {
        console.error('leave community channel failed', error);
      }
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="text-content-1 border-line bg-surface-1">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-content-1">
            Leave Channel
          </AlertDialogTitle>
          <AlertDialogDescription className="text-content-2">
            Are you sure you want to leave this channel?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={pending}
            className="text-content-1 border-line-strong bg-surface-1 hover:bg-brand-hover-soft"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={event => {
              // Keep the dialog mounted until the async leave settles.
              event.preventDefault();
              handleConfirm();
            }}
            className="bg-danger text-content-on-danger hover:bg-red-600"
          >
            {pending ? 'Leaving…' : 'Leave'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
